import { useEffect, useRef, useState } from "react";
import { Button, controlToastState, Select, TextInput } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../api/firebase/references";
import {
  AuthGridPayload,
  fetchAuthGridData,
} from "../../../../api/postCall/authGridApi";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import { NarAgentGridData } from "../../../../data-model/medicalPaOrdersModel";
import { OrganizationType } from "../../../../enums/organizationTypes";
import NarGridBg from "../../../../svg/nar-grid-bg";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../utils/stringModifications";
import NarAgentGridTable from "./components/narAgentGridTable";

interface NarAgentGridLayoutProps {
  onComplete?: boolean;
}

const NarAgentGridLayout = (props: NarAgentGridLayoutProps) => {
  const narAgentGridTableRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>("");
  const [insuranceOptions, setInsuranceOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [selectedInsurance, setSelectedInsurance] = useState<string>("");
  const [drugsInput, setDrugsInput] = useState<string>("");
  const [isLoadingAuthGrid, setIsLoadingAuthGrid] = useState<boolean>(false);
  const [apiData, setApiData] = useState<NarAgentGridData[]>([]);

  const fetchInsuranceOptions = async () => {
    try {
      const options = await FirestoreService.getDocument(
        FirestoreDocumentReference.insuranceOptions("payer_mapping"),
      );

      const payerKeys = Object.keys(options as string)
        .filter((key) => key !== "id")
        .sort();

      const formattedOptions = payerKeys.map((key: string) => ({
        value: key,
        label: capitalizeWordsSeperatedByUnderScore(key),
      }));

      setInsuranceOptions(formattedOptions);
    } catch (error) {
      console.error("Error fetching insurance options:", error);
    }
  };

  const fetchAuthGrid = async () => {
    setIsLoadingAuthGrid(true);
    try {
      const jCodes = drugsInput
        .split(",")
        .map((code) => code.trim())
        .filter((code) => code.length > 0);

      const payload: AuthGridPayload = {
        payer_name: selectedInsurance,
        j_codes: jCodes.length > 0 ? jCodes : [],
        client_id: activeTab,
      };

      const response = await fetchAuthGridData(payload);

      if (response.success) {
        setApiData(response.data || []);
        if (response.data && response.data.length === 0) {
          controlToastState("auth-grid-no-data");
        } else {
          controlToastState("auth-grid-success");
        }
      } else {
        console.error("Failed to fetch auth grid data:", response.message);
        controlToastState("auth-grid-failure");
      }
    } catch (error) {
      console.error("Error fetching auth grid data:", error);
      controlToastState("auth-grid-error");
    } finally {
      setIsLoadingAuthGrid(false);
    }
  };

  useEffect(() => {
    fetchInsuranceOptions();
    setActiveTab(OrganizationType.NYCBS);
  }, []);

  useEffect(() => {
    if (activeTab) {
      setApiData([]);
      setSelectedInsurance("");
      setDrugsInput("");
    }
  }, [activeTab]);

  const tabElements = [
    { id: OrganizationType.NYCBS, label: "NYCBS" },
    { id: OrganizationType.CHC, label: "CHC" },
    { id: OrganizationType.MBP, label: "MBP" },
    { id: OrganizationType.UCBC, label: "UCBC" },
  ];

  return (
    <div className="nar-agent-grid--main-container flex h-screen max-h-screen flex-col overflow-hidden">
      <div className="nar-agent-grid__header mb-4 flex items-end justify-between text-h11 font-bold text-primaryGray-1">
        NAR Agent Grid
      </div>

      {/* Tab Navigation */}
      <div className="nar-agent-grid--tabs flex flex-row border-b border-primaryGray-14 pt-2">
        {tabElements.map((tab) => (
          <div
            key={tab.id}
            className={`nar-agent-grid--tab cursor-pointer px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "border-b-2 border-tertiaryBlue-4 bg-tertiaryBlue-13 font-bold text-tertiaryBlue-4"
                : "text-primaryGray-6 hover:border-transparent"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div className="nar-agent-grid__content flex w-full flex-1 flex-col overflow-hidden">
        <div className="nar-agent-grid__table-container flex flex-1 flex-col overflow-hidden p-4">
          <div className="mb-4 flex w-full flex-row gap-4">
            <div className="w-1/4">
              <Select
                key={`insurance-${activeTab}`}
                id="insurance"
                label="Insurance"
                options={insuranceOptions}
                value={selectedInsurance}
                onOptionChange={(option) => setSelectedInsurance(option.value)}
                placeholder="Select Insurance"
              />
            </div>
            <div className="w-1/4">
              <TextInput
                id="drugs"
                label="Drugs (J-Codes)"
                defaultValue={drugsInput}
                onChange={(value) => setDrugsInput(value.value)}
                placeholder="Enter J-codes separated by commas"
              />
            </div>
            <div className="mt-6 w-auto">
              <Button
                buttonType="secondary"
                size="medium"
                disabled={isLoadingAuthGrid}
                onClick={() => fetchAuthGrid()}
              >
                Search
              </Button>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            {isLoadingAuthGrid ? (
              <div className="flex flex-1 items-center justify-center">
                <LoaderMessage message="Loading Auth Grid" />
              </div>
            ) : apiData.length > 0 ? (
              <div className="flex-1 overflow-hidden">
                <NarAgentGridTable
                  narAgentGridTableRef={narAgentGridTableRef}
                  clientId={activeTab}
                  exportingInProgress={false}
                  apiData={apiData}
                />
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center">
                <div className="mb-2 text-lg font-medium">
                  <NarGridBg />
                </div>
                <div className="text-center text-small text-primaryGray-3">
                  Select the Insurance and enter the J-codes to view the results
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NarAgentGridLayout;
